import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { StatusPill } from "../common/StatusPill";
import { toast } from "../ui/sonner";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

// UX-CP-01 §3 SCR-P08-14 "Control Plane sets the ceiling, never fills it" — only moves
// `seat_limit`. Requires the 'billing' Kairoo permission.
export function SeatLicensePanel({ orgId, license, seatsUsed, canEdit }) {
  const [seatLimit, setSeatLimit] = useState(license?.seat_limit ?? "");
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/control/gyms/${orgId}/seat-limit`, { seat_limit: Number(seatLimit) }),
    onSuccess: () => {
      toast.success("Seat limit updated");
      queryClient.invalidateQueries({ queryKey: ["control-gym-detail", orgId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <motion.div {...fadeUp(reducedMotion)} className="solid-surface rounded-[14px] p-5 space-y-4">
      <div>
        <p className="eyebrow mb-2">License</p>
        <div className="flex items-center gap-3">
          <StatusPill status={license?.state} testId={CONTROL.licenseStateBadge} />
          <span className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
            Valid to: {license?.valid_to ? new Date(license.valid_to).toLocaleDateString() : "No expiry set"}
          </span>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="seat-limit-input">Seat limit ({seatsUsed} used)</Label>
        <div className="flex items-center gap-2">
          <Input
            id="seat-limit-input"
            type="number"
            min={seatsUsed || 1}
            data-testid={CONTROL.seatLimitInput}
            value={seatLimit}
            disabled={!canEdit}
            onChange={(e) => setSeatLimit(e.target.value)}
            className="max-w-[140px]"
          />
          <Button
            data-testid={CONTROL.seatLimitSave}
            disabled={!canEdit || saveMutation.isPending || Number(seatLimit) === license?.seat_limit}
            onClick={() => saveMutation.mutate()}
            className="text-white"
            style={{ background: "var(--scarlet-accent)" }}
          >
            Save
          </Button>
        </div>
        <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
          The Control Plane only sets the ceiling — the gym's own Staff page fills seats within it.
        </p>
      </div>
      {!canEdit && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Requires the 'billing' Kairoo permission to edit.</p>}
    </motion.div>
  );
}
